import { ACCEPTED_IMAGE_MIME_TYPES, type MediaAsset, type MediaPurpose } from '@recipes/contracts';
import { api } from '@/lib/api';

export type UploadPhase = 'intent' | 'uploading' | 'processing' | 'ready' | 'failed';

export interface UploadOptions {
  purpose: MediaPurpose;
  altText?: string | null;
  onPhase?: (phase: UploadPhase) => void;
  signal?: AbortSignal;
  /** Maximum time to wait for the worker to produce variants (default 45 s). */
  processingTimeoutMs?: number;
}

export class UploadValidationError extends Error {
  constructor(public readonly reason: 'type' | 'size') {
    super(reason === 'type' ? 'Unsupported file type' : 'File too large');
    this.name = 'UploadValidationError';
  }
}

const ACCEPTED = new Set<string>(ACCEPTED_IMAGE_MIME_TYPES);
const EXT_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

/** Browsers sometimes report an empty type for HEIC; derive it from the extension. */
export function detectMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_TO_MIME[ext] ?? '';
}

export function validateImageFile(file: File, maxBytes: number | null): void {
  if (!ACCEPTED.has(detectMimeType(file))) throw new UploadValidationError('type');
  if (maxBytes !== null && file.size > maxBytes) throw new UploadValidationError('size');
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Poll until the worker produced variants (READY) or gave up (FAILED). */
export async function waitUntilReady(
  assetId: string,
  initial: MediaAsset,
  timeoutMs = 45_000,
  signal?: AbortSignal,
): Promise<MediaAsset> {
  let asset = initial;
  const started = Date.now();
  let delay = 800;
  while (asset.status !== 'READY' && asset.status !== 'FAILED') {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (Date.now() - started > timeoutMs) return asset;
    await sleep(delay);
    delay = Math.min(delay * 1.5, 4000);
    asset = await api.media.get(assetId);
  }
  return asset;
}

/**
 * Upload intent → PUT bytes → complete → wait for processing. Returns the asset, which may
 * still be PROCESSING when the worker is slow; the UI shows a pending state in that case.
 */
export async function uploadImage(file: File, options: UploadOptions): Promise<MediaAsset> {
  const mimeType = detectMimeType(file);
  options.onPhase?.('intent');
  const intent = await api.media.createUploadIntent({
    fileName: file.name || `image.${mimeType.split('/')[1] ?? 'jpg'}`,
    mimeType,
    sizeBytes: file.size,
    purpose: options.purpose,
  });
  if (file.size > intent.maxBytes) throw new UploadValidationError('size');
  options.onPhase?.('uploading');
  await api.media.upload(intent, file, options.signal);
  options.onPhase?.('processing');
  const completed = await api.media.complete(intent.assetId, { altText: options.altText ?? null });
  const asset = await waitUntilReady(
    intent.assetId,
    completed,
    options.processingTimeoutMs,
    options.signal,
  );
  options.onPhase?.(asset.status === 'FAILED' ? 'failed' : 'ready');
  return asset;
}
